// EnvironmentBaselineGate — full-screen wizard that gates the test until
// the candidate has completed a 360° room scan (or until the feature
// flag is off, in which case it auto-resolves with onPass()).
//
// Same modal-overlay UX as IdentityGate so candidates see consistent
// pre-test flow. Mounts AFTER identity verification and BEFORE the
// assessment instructions modal.
//
// State machine:
//   'loading'   → spinner while config + status hydrate
//   'skipped'   → no sub-features enabled → call onPass() immediately
//   'passed'    → server says verdict='passed' → call onPass()
//   'ready'     → render RoomScan with "Start 360° scan" button
//   'scanning'  → RoomScan is mid-sweep, capturing frames
//   'analyzing' → finalize POST in flight (Rekognition fan-out)
//   'failed'    → server returned verdict='failed' → show retry UI
//   'error'     → network / config failure → retry button
//
// Props:
//   testId          — required, drives the server row identity
//   applicationId?  — optional, for dashboard joins
//   candidateEmail? — optional, recorded with the baseline
//   onPass()        — called once the gate is cleared

import React, { useEffect } from 'react';
import {
  Camera, CheckCircle2, AlertTriangle, XCircle, Loader2, RefreshCw,
} from 'lucide-react';
import { useEnvironmentBaseline } from './useEnvironmentBaseline';
import RoomScan from './RoomScan';


export default function EnvironmentBaselineGate({
  testId,
  applicationId = null,
  candidateEmail = null,
  onPass,
}) {
  const {
    config, row, status, error, busy,
    start, uploadFrame, finalize, reset, refresh,
  } = useEnvironmentBaseline(testId, { applicationId, candidateEmail });

  // Auto-resolve when the gate has nothing to enforce.
  useEffect(() => {
    if (status === 'skipped' || status === 'passed') {
      onPass?.();
    }
  }, [status, onPass]);

  if (status === 'skipped' || status === 'passed') return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <header className="flex items-center gap-2 px-6 py-4 border-b border-gray-200">
          <Camera className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            Environment scan
          </h2>
        </header>

        <div className="px-6 py-5 space-y-5">
          <Body
            status={status}
            error={error}
            busy={busy}
            row={row}
            config={config}
            onStart={start}
            onFrame={uploadFrame}
            onComplete={finalize}
            onReset={() => { reset(); void refresh(); }}
            onRetryAfterFailed={async () => {
              // For a fresh retry, reset server state by calling /start.
              // The RoomScan component will then re-init the streams.
              await start();
            }}
          />
        </div>

        <footer className="px-6 py-3 border-t border-gray-200 text-xs text-gray-500">
          The frames captured here are kept for 90 days, encrypted at rest,
          and reviewed only if your test is flagged for cheating concerns.
        </footer>
      </div>
    </div>
  );
}


function Body({
  status, error, busy, row, config,
  onStart, onFrame, onComplete, onReset, onRetryAfterFailed,
}) {
  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center py-10 text-gray-600">
        <Loader2 className="w-6 h-6 mr-2 animate-spin" />
        Loading…
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
          <div className="text-sm text-red-900">
            <div className="font-medium">Couldn't load environment-baseline status</div>
            <div className="mt-1">{error || 'Unknown error'}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <XCircle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-red-900">
              Environment scan didn't succeed
            </div>
            <div className="text-red-800 mt-1">
              {row?.verdict_reason ||
                "We couldn't analyse your room scan. Please try again with better lighting and a slow full rotation."}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onRetryAfterFailed}
          disabled={busy}
          className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Resetting…
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Try the scan again
            </>
          )}
        </button>
      </div>
    );
  }

  // 'ready' OR 'scanning' OR 'analyzing' — RoomScan handles all three phases.
  return (
    <RoomScan
      minSweepSeconds={config?.min_sweep_seconds || 10}
      captureEveryMs={1500}
      onStart={onStart}
      onFrame={onFrame}
      onComplete={onComplete}
      busy={busy}
    />
  );
}
